import {Button, Group, Modal, Stack, Text, useMantineTheme, rgba, TextInput, Checkbox} from '@mantine/core';
import {
    PasskeyService,
    Registration,
    RegistrationMailService,
    RegistrationService,
    StaffService,
    StatisticsService,
    UserService,
} from '../../client';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {DataTable, DataTableSortStatus} from 'mantine-datatable';
import sortBy from 'lodash/sortBy';
import {useEffect, useMemo, useState} from "react";
import {useDebouncedValue, useDisclosure, useMediaQuery} from "@mantine/hooks";
import {ActionIcon} from "@mantine/core";
import {IconX, IconSearch} from "@tabler/icons-react";
import {notifications} from '@mantine/notifications';
import {formatAdminDate} from '../../utils/formatDate';
import {PageLayout} from '../shell/PageLayout';
import {
    BooleanStatusIcon,
    DeleteTableAction,
    EditTableAction,
    PaidToggleAction,
    StaffToggleAction,
} from '../common/TableActionIcons';
import {
    buildStaffByEmailMap,
    buildStaffEmailSet,
    filterRegistrationsByStaffVisibility,
    isStaffRegistration,
} from '../../utils/staffRegistrationFilter';
import type {StaffMember} from '../staff/Staff';
import {RegistrationProgress} from './RegistrationProgress';
import {RegistrationEditModal} from './RegistrationEditModal';
import {RegistrationMailSendProgress} from './RegistrationMailSendProgress';
import {openRegistrationMailSendModal} from './RegistrationMailSendModal';
import {canWriteRegistrations, normalizeRole} from '../../utils/roles';
import {useDataTablePagination} from '../../utils/useDataTablePagination';
import {useAuthStore} from '../../store/authStore';
import {jwtDecode} from 'jwt-decode';

export const Registrations = () => {
    const token = useAuthStore(state => state.token);
    const username = useMemo(() => {
        if (!token) {
            return '';
        }
        try {
            return jwtDecode<{sub?: string}>(token).sub ?? '';
        } catch {
            return '';
        }
    }, [token]);

    const {isLoading, data} = useQuery({
        queryKey: ['registrations'],
        queryFn: RegistrationService.getAllRegistrationsApiV1RegistrationsGet,
    });

    const {data: currentUserProfile} = useQuery({
        queryKey: ['current-user'],
        queryFn: UserService.getCurrentUserApiV1UserMeGet,
    });
    const canWrite = canWriteRegistrations(normalizeRole(currentUserProfile?.role));

    const {data: passkeyStatus} = useQuery({
        queryKey: ['passkey-status'],
        queryFn: PasskeyService.passkeyStatusApiV1PasskeyStatusGet,
        enabled: canWrite,
    });

    const {data: registrationMailSendStatus} = useQuery({
        queryKey: ['registration-mail-send-status'],
        queryFn: RegistrationMailService.getRegistrationMailSendStatusApiV1RegistrationMailSendStatusGet,
        enabled: canWrite,
        refetchInterval: (query) => (query.state.data?.sending ? 5000 : false),
    });

    const {data: staffData} = useQuery({
        queryKey: ['staff'],
        queryFn: async () => {
            const response = await StaffService.getAllStaffApiV1StaffGet();
            return response as { staff_members: StaffMember[] };
        },
        enabled: canWrite,
    });

    const {data: registrationStats} = useQuery({
        queryKey: ['registration-stats'],
        queryFn: StatisticsService.getRegistrationsApiV1StatsRegistrationsGet,
    });

    const staffEmails = useMemo(
        () => buildStaffEmailSet(staffData?.staff_members),
        [staffData],
    );

    const staffByEmail = useMemo(
        () => buildStaffByEmailMap(staffData?.staff_members),
        [staffData],
    );

    const isMobile = useMediaQuery('(max-width: 768px)');
    const [registrations, setRegistrations] = useState<Registration[]>([]);
    const queryClient = useQueryClient();
    const theme = useMantineTheme();
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Registration>>({
        columnAccessor: 'timestamp',
        direction: 'desc',
    });
    const {page, setPage, recordsPerPage, getPaginationProps} = useDataTablePagination();
    const [records, setRecords] = useState<Registration[]>([]);
    const [nameQuery, setNameQuery] = useState('');
    const [mailQuery, setMailQuery] = useState('');
    const [idQuery, setIdQuery] = useState('');
    const [commentQuery, setCommentQuery] = useState('');
    const [onlyShowPaid, toggleOnlyShowPaid] = useState(false);
    const [showStaffRegistrations, setShowStaffRegistrations] = useState(true);
    const [editingRegistration, setEditingRegistration] = useState<Registration | null>(null);
    const [paymentReminderLoading, setPaymentReminderLoading] = useState(false);
    const [paymentReminderModalOpened, {
        open: openPaymentReminderModal,
        close: closePaymentReminderModal,
    }] = useDisclosure(false);
    const [debouncedNameQuery] = useDebouncedValue(nameQuery, 200);
    const [debouncedMailQuery] = useDebouncedValue(mailQuery, 200);
    const [debouncedIdQuery] = useDebouncedValue(idQuery, 200);
    const [debouncedCommentQuery] = useDebouncedValue(commentQuery, 200);

    const togglePaid = (registration: Registration) => {
        RegistrationService.togglePaidApiV1RegistrationRegIdTogglePaidPut({
            regId: registration.id,
        }).then(() => {
            queryClient.invalidateQueries({queryKey: ['registrations']});
        });
    };

    const deleteRegistration = (registration: Registration) => {
        RegistrationService.deleteRegistrationByIdApiV1RegistrationRegIdDelete(
            {
                regId: registration.id,
            }
        ).then(() => {
            queryClient.invalidateQueries({queryKey: ['registrations']});
        })
    }

    const toggleStaff = (registration: Registration) => {
        const emailKey = registration.email.toLowerCase();
        const existingStaff = staffByEmail.get(emailKey);
        const request = existingStaff
            ? StaffService.deleteStaffApiV1StaffStaffIdDelete({staffId: existingStaff.id})
            : StaffService.addStaffApiV1StaffPost({
                requestBody: {
                    email: registration.email,
                    firstname: registration.firstname,
                    lastname: registration.lastname,
                },
            });

        request
            .then(() => {
                queryClient.invalidateQueries({queryKey: ['registrations']});
                queryClient.invalidateQueries({queryKey: ['staff']});
            })
            .catch((error: { body?: { detail?: string }; message?: string }) => {
                notifications.show({
                    color: 'red',
                    title: 'Could not update staff status',
                    message: error.body?.detail ?? error.message ?? 'Request failed',
                });
            });
    };

    const runPaymentReminders = () => {
        setPaymentReminderLoading(true);
        RegistrationService.markPaymentsAsDelayedApiV1RegistrationMarkPaymentsPost()
            .then((response) => {
                const delayedPayments = (response as { delayed_payments?: unknown[] })?.delayed_payments;
                const count = Array.isArray(delayedPayments) ? delayedPayments.length : 0;
                queryClient.invalidateQueries({queryKey: ['registrations']});
                queryClient.invalidateQueries({queryKey: ['registration-stats']});
                notifications.show({
                    color: count > 0 ? 'green' : 'blue',
                    title: 'Payment reminders processed',
                    message: count > 0
                        ? `${count} registration(s) marked as delayed and notified.`
                        : 'No overdue registrations found.',
                });
                closePaymentReminderModal();
            })
            .catch((error: { body?: { detail?: string }; message?: string }) => {
                notifications.show({
                    color: 'red',
                    title: 'Could not send payment reminders',
                    message: error.body?.detail ?? error.message ?? 'Request failed',
                });
            })
            .finally(() => {
                setPaymentReminderLoading(false);
            });
    };

    useEffect(() => {
        const sorted = sortBy(data ?? [], sortStatus.columnAccessor) as Registration[];
        const visible = filterRegistrationsByStaffVisibility(
            sorted,
            staffEmails,
            showStaffRegistrations,
        );
        const mapped = visible
            .filter((registration: Registration) => {
                if (debouncedNameQuery === '') {
                    return true;
                }
                const lowercase: string = debouncedNameQuery.toLowerCase();
                const name: string = registration.firstname.toLowerCase();
                const lastname: string = registration.lastname.toLowerCase();
                return name.includes(lowercase) || lastname.includes(lowercase) || `${lastname} ${name}`.includes(lowercase) || `${name} ${lastname}`.includes(lowercase);
            })
            .filter((registration: Registration) => {
                if (debouncedMailQuery === '') {
                    return true;
                }
                const lowercase: string = debouncedMailQuery.toLowerCase();
                const mail: string = registration.email.toLowerCase();
                return mail.includes(lowercase);
            })
            .filter((registration: Registration) => {
                if (debouncedIdQuery === '') {
                    return true;
                }
                return registration.id.toString().includes(debouncedIdQuery);
            })
            .filter((registration: Registration) => {
                if (debouncedCommentQuery === '') {
                    return true;
                }
                return registration.comment?.toLowerCase().includes(debouncedCommentQuery.toLowerCase());
            })
            .filter((registration: Registration) => {
                if (!onlyShowPaid) {
                    return true;
                }
                return registration.paid === 'paid';
            })
            .map((registration: Registration) => ({
                ...registration,
                name: `${registration.lastname}, ${registration.firstname}`,
                address: `${registration.address}, ${registration.city}`
            }));
        const tmp = sortStatus.direction === 'desc' ? mapped.reverse() : mapped;
        setRegistrations(tmp);
        const from = (page - 1) * recordsPerPage;
        const to = from + recordsPerPage;
        setRecords(tmp.slice(from, to));
    }, [
        debouncedNameQuery,
        sortStatus,
        data,
        page,
        recordsPerPage,
        debouncedMailQuery,
        debouncedIdQuery,
        debouncedCommentQuery,
        onlyShowPaid,
        showStaffRegistrations,
        staffEmails,
    ]);


    return (
        <PageLayout
            title="Registrations"
            description="Manage camp registrations, payments, and participant details."
            actions={canWrite ? (
                <Group gap="xs">
                    <Button
                        variant="outline"
                        onClick={() => openRegistrationMailSendModal({
                            username,
                            hasPasskey: passkeyStatus?.has_passkey ?? false,
                            sendInProgress: registrationMailSendStatus?.sending ?? false,
                        })}
                        disabled={registrationMailSendStatus?.sending}
                    >
                        Send mail
                    </Button>
                    <Button
                        variant="outline"
                        onClick={openPaymentReminderModal}
                    >
                        Send payment reminders
                    </Button>
                </Group>
            ) : undefined}
        >
            <Stack gap="md">
                <RegistrationMailSendProgress
                    status={registrationMailSendStatus}
                    canCancel={canWrite}
                />
                <RegistrationProgress stats={registrationStats} />
            <DataTable
                withTableBorder
                striped
                highlightOnHover
                records={records}
                fetching={isLoading}
                loaderType='oval'
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                {...getPaginationProps(registrations.length)}
                columns={[
                    {
                        accessor: 'id',
                        title: 'ID',
                        sortable: true,
                        filter: (
                            <TextInput
                                label="Filter"
                                description="Filter by ID"
                                placeholder="Filter registrations..."
                                leftSection={<IconSearch size={16}/>}
                                rightSection={
                                    <ActionIcon size="sm" variant="transparent" c="dimmed"
                                                onClick={() => setIdQuery('')}>
                                        <IconX size={14}/>
                                    </ActionIcon>
                                }
                                value={idQuery}
                                onChange={(e) => setIdQuery(e.currentTarget.value)}
                            />),
                    },
                    {
                        accessor: 'timestamp', title: 'Timestamp', sortable: true, hidden: isMobile,
                        render: ({timestamp}) => formatAdminDate(timestamp),
                    },
                    {
                        accessor: 'name',
                        title: 'Name',
                        sortable: true,
                        filter: (
                            <TextInput
                                label="Filter"
                                description="Filter by Name"
                                placeholder="Filter registrations..."
                                leftSection={<IconSearch size={16}/>}
                                rightSection={
                                    <ActionIcon size="sm" variant="transparent" c="dimmed"
                                                onClick={() => setNameQuery('')}>
                                        <IconX size={14}/>
                                    </ActionIcon>
                                }
                                value={nameQuery}
                                onChange={(e) => setNameQuery(e.currentTarget.value)}
                            />),
                        filtering: nameQuery !== '',
                    },
                    {
                        accessor: 'age',
                        title: 'Age',
                        sortable: true,
                        hidden: isMobile,
                    },
                    {
                        accessor: 'email',
                        title: 'Email',
                        render: ({email}) => (
                            <a href={`mailto:${email}`}>{email}</a>
                        ),
                        hidden: isMobile,
                        filter: (
                            <TextInput
                                label="Filter"
                                description="Filter by mail"
                                placeholder="Filter registrations..."
                                leftSection={<IconSearch size={16}/>}
                                rightSection={
                                    <ActionIcon size="sm" variant="transparent" c="dimmed"
                                                onClick={() => setMailQuery('')}>
                                        <IconX size={14}/>
                                    </ActionIcon>
                                }
                                value={mailQuery}
                                onChange={(e) => setMailQuery(e.currentTarget.value)}
                            />),
                        filtering: mailQuery !== '',
                    },
                    {
                        accessor: 'phone',
                        title: 'Phone',
                        render: ({phone}) => {
                            const withPlus = `+${phone}`.replace("++", '+');
                            return <a href={`tel:${withPlus}`}>{withPlus}</a>;
                        },
                        hidden: isMobile,
                    },
                    {
                        accessor: 'address',
                        title: 'Address',
                        sortable: true,
                        hidden: isMobile,
                    },
                    {
                        accessor: 'food',
                        title: 'Food',
                        sortable: true,
                        hidden: isMobile,
                    },
                    {
                        accessor: 'tshirt',
                        title: 'T-Shirt',
                        sortable: true,
                        hidden: isMobile,
                        render: ({tshirt: tShirt}) => tShirt?.toUpperCase() ?? '',
                    },
                    {
                        accessor: 'picture',
                        title: 'Picture',
                        render: ({picture}) => (
                            <BooleanStatusIcon
                                value={Boolean(picture)}
                                trueLabel="Picture uploaded"
                                falseLabel="No picture"
                                falseVariant="blocked"
                            />
                        ),
                        sortable: true,
                        hidden: isMobile,
                    },
                    {
                        accessor: 'comment',
                        title: 'Comment',
                        filter: (
                            <TextInput
                                label="Filter"
                                description="Filter by Comment"
                                placeholder="Filter registrations..."
                                leftSection={<IconSearch size={16}/>}
                                rightSection={
                                    <ActionIcon size="sm" variant="transparent" c="dimmed"
                                                onClick={() => setCommentQuery('')}>
                                        <IconX size={14}/>
                                    </ActionIcon>
                                }
                                value={commentQuery}
                                onChange={(e) => setCommentQuery(e.currentTarget.value)}
                            />),
                    },
                    {
                        accessor: 'checked_in',
                        title: 'Checked-In',
                        render: ({checked_in}) => (
                            <BooleanStatusIcon
                                value={Boolean(checked_in)}
                                trueLabel="Checked in"
                                falseLabel="Not checked in"
                            />
                        ),
                        sortable: true,
                        hidden: isMobile,
                    },
                    ...(canWrite ? [
                    {
                        accessor: 'mark-staff',
                        title: '',
                        render: (record: Registration) => (
                            <StaffToggleAction
                                isStaff={staffEmails.has(record.email.toLowerCase())}
                                onClick={() => toggleStaff(record)}
                            />
                        ),
                    },
                    {
                        accessor: 'toggle-paid',
                        title: '',
                        render: (record: Registration) => (
                            <PaidToggleAction
                                isPaid={record.paid === 'paid'}
                                onClick={() => togglePaid(record)}
                            />
                        ),
                        filter: (
                            <Stack gap="xs">
                                <Checkbox
                                    label="Only show paid registrations."
                                    checked={onlyShowPaid}
                                    onChange={(e) => toggleOnlyShowPaid(e.currentTarget.checked)}
                                />
                                <Checkbox
                                    label="Show staff registrations"
                                    checked={showStaffRegistrations}
                                    onChange={(e) => setShowStaffRegistrations(e.currentTarget.checked)}
                                />
                            </Stack>),
                    },
                    {
                        accessor: 'edit-registration',
                        title: '',
                        render: (record: Registration) => (
                            <EditTableAction
                                onClick={() => setEditingRegistration(record)}
                            />
                        ),
                    },
                    {
                        accessor: 'delete-registration',
                        title: '',
                        render: (record: Registration) => (
                            <DeleteTableAction
                                onClick={() => {
                                    if (window.confirm('Are you sure you want to delete this registration?')) {
                                        deleteRegistration(record);
                                    }
                                }}
                                label="Delete registration"
                            />
                        ),
                    },
                    ] : []),
                ]}
                rowStyle={(record) => {
                    if (isStaffRegistration(record, staffEmails)) {
                        return {backgroundColor: rgba(theme.colors.violet[8], 0.2)};
                    }
                    if (record.paid === 'paid') {
                        return {backgroundColor: rgba(theme.colors.green[8], 0.25)};
                    }
                    if (record.paid === 'delayed') {
                        return {backgroundColor: rgba(theme.colors.red[8], 0.25)};
                    }
                    return undefined;
                }}
            />
            <RegistrationEditModal
                registration={editingRegistration}
                opened={editingRegistration !== null}
                onClose={() => setEditingRegistration(null)}
                onSaved={() => {
                    queryClient.invalidateQueries({queryKey: ['registrations']});
                    queryClient.invalidateQueries({queryKey: ['staff']});
                }}
            />
            <Modal
                opened={paymentReminderModalOpened}
                onClose={() => {
                    if (!paymentReminderLoading) {
                        closePaymentReminderModal();
                    }
                }}
                title="Send payment reminders"
                centered
                closeOnClickOutside={!paymentReminderLoading}
                closeOnEscape={!paymentReminderLoading}
            >
                <Stack gap="md">
                    <Text size="sm">
                        Mark overdue registrations as delayed and send payment reminder emails?
                    </Text>
                    <Group justify="flex-end">
                        <Button
                            variant="default"
                            onClick={closePaymentReminderModal}
                            disabled={paymentReminderLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="orange"
                            loading={paymentReminderLoading}
                            onClick={runPaymentReminders}
                        >
                            Send reminders
                        </Button>
                    </Group>
                </Stack>
            </Modal>
            </Stack>
        </PageLayout>
    );
};
